/**
 * June Moon Anti-Bot Protection System v1.0.0
 * Production-ready client-side bot detection for Shopify storefronts.
 *
 * Placement: Upload to Shopify Assets → load via theme.liquid snippet.
 * Kill switch: set window.JUNE_MOON_ANTIBOT_ENABLED = false before this script loads.
 *
 * IMPORTANT LIMITATIONS (honest disclosure):
 *   - Client-side JS cannot stop bots that never execute JavaScript.
 *   - A determined attacker can reverse-engineer or disable this script.
 *   - True IP-based rate limiting requires a WAF/proxy (Cloudflare, etc.).
 *   - This layer raises the cost of abuse significantly and stops 90%+ of
 *     commodity bot traffic (ad-click bots, headless scrapers, automation tools).
 */
;(function () {
  'use strict';

  /* =========================================================================
   * 0. KILL SWITCH
   * ========================================================================= */
  if (window.JUNE_MOON_ANTIBOT_ENABLED === false) return;

  /* =========================================================================
   * 1. CONFIGURATION — all tunables in one place
   * ========================================================================= */
  var CONFIG = {
    // --- Risk thresholds (0-100) ---
    thresholds: {
      low:      25,   // 0-24  → normal access
      medium:   50,   // 25-49 → silent monitoring
      high:     70,   // 50-69 → add friction / challenge
      veryHigh: 85    // 70+   → soft block
    },

    // --- Rate limiting ---
    rate: {
      maxPagesPerMinute:    20,   // pages loaded within 60s window
      maxPagesPerSession:  200,   // total pages in a single session
      rapidLoadThresholdMs: 800,  // page loads faster than this = suspicious
      sessionWindowMs:  1800000   // 30 min session window
    },

    // --- Challenge ---
    challenge: {
      holdDurationMs:     1500,   // press-and-hold duration to pass
      verifiedTTLMs: 86400000,    // 24h — verified cookie lifetime
      maxAttemptsBeforeBlock: 3   // failed challenges before soft block
    },

    // --- Behavior ---
    behavior: {
      interactionGracePeriodMs: 3000,  // wait this long before scoring no-interaction
      mouseMoveMinEvents:          3,  // desktop: expect at least N mouse events
      scrollMinEvents:             1
    },

    // --- Timing ---
    timing: {
      firstCheckDelayMs:   2500,  // delay first risk assessment
      recheckIntervalMs:  15000   // periodic re-check
    },

    // --- Logging ---
    logging: {
      enabled:    true,
      debugMode:  false,   // true = floating debug panel + console output
      storageKey: 'jm_ab_logs',
      maxLogEntries: 50
    },

    // --- Soft block ---
    block: {
      tempBlockDurationMs: 600000  // 10 min temporary soft block
    },

    // --- Legitimate Pixel (never touch) ---
    legitimatePixelId: '762480312004956',

    // --- Version ---
    version: '1.0.0'
  };

  // Expose config for runtime tuning via console
  window.__JM_ANTIBOT_CONFIG = CONFIG;

  /* =========================================================================
   * 2. UTILITIES
   * ========================================================================= */
  var Utils = {
    now: function () { return Date.now(); },

    uid: function () {
      return Math.random().toString(36).substring(2, 10) +
             Utils.now().toString(36);
    },

    safeGet: function (fn, fallback) {
      try { return fn(); } catch (e) { return fallback; }
    },

    getCookie: function (name) {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    },

    setCookie: function (name, value, maxAgeMs) {
      var age = Math.round((maxAgeMs || 86400000) / 1000);
      document.cookie = name + '=' + encodeURIComponent(value) +
        ';path=/;max-age=' + age + ';SameSite=Lax;Secure';
    },

    sessionGet: function (key) {
      try { return JSON.parse(sessionStorage.getItem(key)); }
      catch (e) { return null; }
    },

    sessionSet: function (key, val) {
      try { sessionStorage.setItem(key, JSON.stringify(val)); }
      catch (e) { /* quota exceeded — degrade gracefully */ }
    },

    getParam: function (name) {
      var url = new URL(window.location.href);
      return url.searchParams.get(name);
    },

    hashCode: function (str) {
      var h = 0;
      for (var i = 0; i < str.length; i++) {
        h = ((h << 5) - h + str.charCodeAt(i)) | 0;
      }
      return Math.abs(h).toString(36);
    }
  };

  /* =========================================================================
   * 3. LOGGER
   * ========================================================================= */
  var Logger = {
    _entries: [],

    log: function (level, category, message, data) {
      if (!CONFIG.logging.enabled) return;

      var entry = {
        ts:   new Date().toISOString(),
        lvl:  level,
        cat:  category,
        msg:  message,
        page: window.location.pathname,
        ref:  document.referrer || '(direct)',
        src:  TrafficAnalyzer.classify(),
        data: data || null
      };

      this._entries.push(entry);
      if (this._entries.length > CONFIG.logging.maxLogEntries) {
        this._entries.shift();
      }

      // Persist to sessionStorage
      Utils.sessionSet(CONFIG.logging.storageKey, this._entries);

      if (CONFIG.logging.debugMode && typeof console !== 'undefined') {
        var prefix = '[JM-AB][' + level.toUpperCase() + '][' + category + '] ';
        if (level === 'warn' || level === 'error') {
          console.warn(prefix + message, data || '');
        } else {
          console.log(prefix + message, data || '');
        }
      }
    },

    info:  function (cat, msg, d) { this.log('info',  cat, msg, d); },
    warn:  function (cat, msg, d) { this.log('warn',  cat, msg, d); },
    error: function (cat, msg, d) { this.log('error', cat, msg, d); },

    getEntries: function () { return this._entries; },

    // Expose for console debugging
    dump: function () {
      console.table(this._entries);
      return this._entries;
    }
  };

  // Expose logger globally for debugging
  window.__JM_ANTIBOT_LOG = Logger;

  /* =========================================================================
   * 4. TRAFFIC SOURCE ANALYZER
   * ========================================================================= */
  var TrafficAnalyzer = {
    _source: null,

    classify: function () {
      if (this._source) return this._source;

      var ref = (document.referrer || '').toLowerCase();
      var fbclid  = Utils.getParam('fbclid');
      var gclid   = Utils.getParam('gclid');
      var utmSrc  = (Utils.getParam('utm_source')  || '').toLowerCase();
      var utmMed  = (Utils.getParam('utm_medium')   || '').toLowerCase();
      var utmCamp = (Utils.getParam('utm_campaign') || '').toLowerCase();

      var source = 'unknown';

      // Meta / Facebook / Instagram
      if (fbclid || /facebook\.com|fb\.com|instagram\.com|fb\.me|l\.facebook/i.test(ref)) {
        source = utmMed.indexOf('paid') > -1 || utmMed === 'cpc' || utmMed === 'cpm'
          ? 'meta_paid' : 'meta_organic';
        // If fbclid is present but no utm_medium, likely an ad click
        if (fbclid && source === 'meta_organic' && !utmMed) {
          source = 'meta_paid';
        }
      }
      // Google Ads
      else if (gclid || (utmSrc === 'google' && (utmMed === 'cpc' || utmMed === 'ppc'))) {
        source = 'google_ads';
      }
      // Google organic
      else if (/google\./i.test(ref)) {
        source = 'google_organic';
      }
      // Other search engines
      else if (/bing\.com|yahoo\.com|duckduckgo/i.test(ref)) {
        source = 'search_organic';
      }
      // WhatsApp
      else if (/whatsapp|wa\.me/i.test(ref) || utmSrc === 'whatsapp') {
        source = 'whatsapp';
      }
      // Direct
      else if (!ref || ref === '') {
        source = 'direct';
      }
      // Other referral
      else {
        source = 'referral';
      }

      this._source = source;

      // Store UTM data for logging (non-PII)
      this._utmData = {
        source:   utmSrc,
        medium:   utmMed,
        campaign: utmCamp,
        hasFbclid: !!fbclid,
        hasGclid:  !!gclid
      };

      return source;
    },

    getUTMData: function () {
      if (!this._source) this.classify();
      return this._utmData;
    }
  };

  /* =========================================================================
   * 5. ENVIRONMENT DETECTOR
   * Detects headless browsers, automation frameworks, anomalies.
   * Each check returns { score: Number, signal: String } or null.
   * ========================================================================= */
  var EnvironmentDetector = {
    signals: [],

    runAll: function () {
      this.signals = [];
      var checks = [
        this._webdriver,
        this._headlessChrome,
        this._automationFrameworks,
        this._phantomJS,
        this._pluginsAnomaly,
        this._languagesAnomaly,
        this._screenAnomaly,
        this._webglAnomaly,
        this._permissionsAnomaly,
        this._connectionAnomaly,
        this._notificationAnomaly,
        this._canvasNoise,
        this._devtoolsProtocol,
        this._iframeSandbox,
        this._domAutomationFlags
      ];

      for (var i = 0; i < checks.length; i++) {
        var result = Utils.safeGet(checks[i], null);
        if (result && result.score > 0) {
          this.signals.push(result);
        }
      }

      return this.signals;
    },

    totalScore: function () {
      var s = 0;
      for (var i = 0; i < this.signals.length; i++) s += this.signals[i].score;
      return s;
    },

    // --- Individual checks ---

    _webdriver: function () {
      // navigator.webdriver is the strongest single signal
      if (navigator.webdriver === true) {
        return { score: 35, signal: 'navigator.webdriver=true' };
      }
      // Some bots delete it but leave traces
      var desc = Object.getOwnPropertyDescriptor(navigator, 'webdriver');
      if (desc && (desc.get || desc.configurable === false)) {
        return { score: 15, signal: 'webdriver_property_tampered' };
      }
      return null;
    },

    _headlessChrome: function () {
      var ua = navigator.userAgent || '';
      var score = 0;
      var flags = [];

      if (/HeadlessChrome/i.test(ua)) {
        score += 30; flags.push('HeadlessChrome_UA');
      }
      // Chrome without chrome runtime object
      if (/Chrome\//.test(ua) && !window.chrome) {
        score += 15; flags.push('chrome_runtime_missing');
      }
      // Chrome with empty chrome object
      if (window.chrome && !window.chrome.runtime && !window.chrome.app) {
        score += 10; flags.push('chrome_runtime_empty');
      }

      return score > 0 ? { score: Math.min(score, 40), signal: flags.join(', ') } : null;
    },

    _automationFrameworks: function () {
      var markers = [
        'callSelenium', '__selenium_evaluate', '__selenium_unwrapped',
        '_Selenium_IDE_Recorder', '_selenium', 'calledSelenium',
        '__webdriver_script_fn', '__driver_evaluate', '__webdriver_evaluate',
        '__fxdriver_evaluate', '__fxdriver_unwrapped',
        '_phantom', 'callPhantom', '__nightmare',
        '__puppeteer_evaluation_script__',
        '__playwright_evaluation_script__',
        'cdc_adoQpoasnfa76pfcZLmcfl_Array',
        'cdc_adoQpoasnfa76pfcZLmcfl_Promise',
        'cdc_adoQpoasnfa76pfcZLmcfl_Symbol'
      ];

      var score = 0;
      var found = [];

      for (var i = 0; i < markers.length; i++) {
        if (markers[i] in window || markers[i] in document) {
          score += 25;
          found.push(markers[i]);
        }
      }

      return score > 0
        ? { score: Math.min(score, 50), signal: 'automation_markers: ' + found.join(', ') }
        : null;
    },

    _phantomJS: function () {
      if (window.callPhantom || window._phantom || window.__phantomas) {
        return { score: 40, signal: 'phantomjs_detected' };
      }
      return null;
    },

    _pluginsAnomaly: function () {
      var plugins = navigator.plugins;
      // Headless browsers often have 0 plugins
      if (plugins && plugins.length === 0 && !/mobile|android|iphone|ipad/i.test(navigator.userAgent)) {
        return { score: 10, signal: 'zero_plugins_desktop' };
      }
      return null;
    },

    _languagesAnomaly: function () {
      var langs = navigator.languages;
      if (!langs || langs.length === 0) {
        return { score: 10, signal: 'no_languages' };
      }
      // Empty string in languages array
      if (langs[0] === '') {
        return { score: 15, signal: 'empty_primary_language' };
      }
      return null;
    },

    _screenAnomaly: function () {
      var w = screen.width;
      var h = screen.height;
      var cd = screen.colorDepth;
      var score = 0;
      var flags = [];

      // Zero or impossibly small screen
      if (!w || !h || w < 100 || h < 100) {
        score += 15; flags.push('impossible_screen_' + w + 'x' + h);
      }
      // Window larger than screen (common in headless)
      if (window.outerWidth > w + 50 || window.outerHeight > h + 100) {
        score += 8; flags.push('window_exceeds_screen');
      }
      // Unusual color depth
      if (cd && cd < 15) {
        score += 8; flags.push('low_color_depth_' + cd);
      }

      return score > 0 ? { score: score, signal: flags.join(', ') } : null;
    },

    _webglAnomaly: function () {
      var canvas = document.createElement('canvas');
      var gl = Utils.safeGet(function () {
        return canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      }, null);

      if (!gl) {
        // No WebGL at all — could be disabled or headless
        return { score: 5, signal: 'no_webgl' };
      }

      var renderer = Utils.safeGet(function () {
        var dbg = gl.getExtension('WEBGL_debug_renderer_info');
        return dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : '';
      }, '');

      // SwiftShader is used in headless Chrome
      if (/swiftshader|llvmpipe|software/i.test(renderer)) {
        return { score: 20, signal: 'software_renderer: ' + renderer };
      }

      return null;
    },

    _permissionsAnomaly: function () {
      // In headless mode, Notification.permission is often 'denied' by default
      // and navigator.permissions may behave differently
      // This is a weak signal, low score
      if (typeof Notification !== 'undefined' && Notification.permission === 'denied' &&
          navigator.userAgent && /Chrome/.test(navigator.userAgent)) {
        // Only mildly suspicious
        return { score: 3, signal: 'notification_denied_chrome' };
      }
      return null;
    },

    _connectionAnomaly: function () {
      var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn && conn.rtt === 0) {
        return { score: 8, signal: 'zero_rtt' };
      }
      return null;
    },

    _notificationAnomaly: function () {
      // Overridden Notification constructor is suspicious
      if (typeof Notification !== 'undefined') {
        var nStr = Utils.safeGet(function () { return Notification.toString(); }, '');
        if (nStr && nStr.indexOf('[native code]') === -1 && nStr.indexOf('class') === -1) {
          return { score: 10, signal: 'notification_overridden' };
        }
      }
      return null;
    },

    _canvasNoise: function () {
      // Check if canvas toDataURL returns consistent "too perfect" results
      // Bots sometimes have identical canvas output or return empty
      var canvas = document.createElement('canvas');
      canvas.width = 50; canvas.height = 50;
      var ctx = Utils.safeGet(function () { return canvas.getContext('2d'); }, null);
      if (!ctx) return { score: 5, signal: 'no_canvas_2d' };

      ctx.fillStyle = '#f0f';
      ctx.fillRect(0, 0, 25, 25);
      ctx.fillStyle = '#0ff';
      ctx.arc(25, 25, 15, 0, Math.PI * 2);
      ctx.fill();
      ctx.font = '12px Arial';
      ctx.fillText('jm', 5, 40);

      var data = Utils.safeGet(function () { return canvas.toDataURL(); }, '');
      if (!data || data.length < 100) {
        return { score: 12, signal: 'canvas_empty_or_tiny' };
      }

      return null;
    },

    _devtoolsProtocol: function () {
      // Detect Chrome DevTools Protocol runtime injections
      if (window.__cdp_binding__ || window.__crWeb || window.__gCrWeb) {
        return { score: 10, signal: 'cdp_binding_detected' };
      }
      return null;
    },

    _iframeSandbox: function () {
      // If we're in a sandboxed iframe, could be suspicious
      // but also could be legitimate embed — low score
      if (window.self !== window.top) {
        var score = 3;
        try { var x = window.top.location.href; }
        catch (e) { score = 5; } // cross-origin iframe
        return { score: score, signal: 'running_in_iframe' };
      }
      return null;
    },

    _domAutomationFlags: function () {
      // document.documentElement attributes set by automation
      var el = document.documentElement;
      if (el) {
        if (el.getAttribute('webdriver') === 'true') {
          return { score: 30, signal: 'dom_webdriver_attr' };
        }
        if (el.hasAttribute('selenium')) {
          return { score: 30, signal: 'dom_selenium_attr' };
        }
      }
      return null;
    }
  };

  /* =========================================================================
   * 6. BEHAVIOR ANALYZER
   * Tracks real user interaction signals.
   * ========================================================================= */
  var BehaviorAnalyzer = {
    _state: {
      mouseEvents:  0,
      scrollEvents: 0,
      clickEvents:  0,
      keyEvents:    0,
      touchEvents:  0,
      firstInteractionTs: null,
      pageLoadTs:   Utils.now(),
      mousePath:    [],       // last N positions for entropy check
      scrollPositions: [],
      hasInteracted: false
    },

    init: function () {
      var self = this;

      var onMouse = function (e) {
        self._state.mouseEvents++;
        if (!self._state.firstInteractionTs) self._state.firstInteractionTs = Utils.now();
        self._state.hasInteracted = true;
        if (self._state.mousePath.length < 20) {
          self._state.mousePath.push({ x: e.clientX, y: e.clientY, t: Utils.now() });
        }
      };

      var onScroll = function () {
        self._state.scrollEvents++;
        if (!self._state.firstInteractionTs) self._state.firstInteractionTs = Utils.now();
        self._state.hasInteracted = true;
        if (self._state.scrollPositions.length < 10) {
          self._state.scrollPositions.push(window.scrollY);
        }
      };

      var onClick = function () {
        self._state.clickEvents++;
        if (!self._state.firstInteractionTs) self._state.firstInteractionTs = Utils.now();
        self._state.hasInteracted = true;
      };

      var onKey = function () {
        self._state.keyEvents++;
        if (!self._state.firstInteractionTs) self._state.firstInteractionTs = Utils.now();
        self._state.hasInteracted = true;
      };

      var onTouch = function () {
        self._state.touchEvents++;
        if (!self._state.firstInteractionTs) self._state.firstInteractionTs = Utils.now();
        self._state.hasInteracted = true;
      };

      document.addEventListener('mousemove', onMouse, { passive: true });
      document.addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('click', onClick, { passive: true });
      document.addEventListener('keydown', onKey, { passive: true });
      document.addEventListener('touchstart', onTouch, { passive: true });
    },

    assess: function () {
      var signals = [];
      var s = this._state;
      var elapsed = Utils.now() - s.pageLoadTs;
      var isMobile = /mobile|android|iphone|ipad|ipod/i.test(navigator.userAgent);

      // Grace period not elapsed yet — skip behavioral scoring
      if (elapsed < CONFIG.behavior.interactionGracePeriodMs) {
        return signals;
      }

      // No interaction at all after grace period (strong on desktop, weaker on mobile)
      if (!s.hasInteracted) {
        var noInteractScore = isMobile ? 5 : 12;
        signals.push({ score: noInteractScore, signal: 'no_interaction_after_' + Math.round(elapsed / 1000) + 's' });
      }

      // Desktop: no mouse movement
      if (!isMobile && s.mouseEvents < CONFIG.behavior.mouseMoveMinEvents && elapsed > 5000) {
        signals.push({ score: 8, signal: 'no_mouse_movement_desktop' });
      }

      // No scroll on a long page
      if (s.scrollEvents < CONFIG.behavior.scrollMinEvents && elapsed > 8000) {
        var pageH = document.documentElement.scrollHeight;
        var viewH = window.innerHeight;
        if (pageH > viewH * 1.5) {
          signals.push({ score: 5, signal: 'no_scroll_long_page' });
        }
      }

      // Mouse path entropy — all movements in same spot (bot clicking same coordinate)
      if (s.mousePath.length >= 5) {
        var xSet = {};
        var ySet = {};
        for (var i = 0; i < s.mousePath.length; i++) {
          xSet[Math.round(s.mousePath[i].x / 10)] = 1;
          ySet[Math.round(s.mousePath[i].y / 10)] = 1;
        }
        var xUnique = Object.keys(xSet).length;
        var yUnique = Object.keys(ySet).length;
        if (xUnique <= 1 && yUnique <= 1) {
          signals.push({ score: 15, signal: 'zero_mouse_entropy' });
        }
      }

      return signals;
    }
  };

  /* =========================================================================
   * 7. RATE LIMITER (session-based)
   * ========================================================================= */
  var RateLimiter = {
    _storageKey: 'jm_ab_rate',

    _getState: function () {
      var state = Utils.sessionGet(this._storageKey);
      if (!state || (Utils.now() - state.sessionStart) > CONFIG.rate.sessionWindowMs) {
        state = {
          sessionStart: Utils.now(),
          pageLoads: [],
          totalPages: 0,
          paths: {}
        };
      }
      return state;
    },

    record: function () {
      var state = this._getState();
      var now = Utils.now();
      state.pageLoads.push(now);
      state.totalPages++;

      var path = window.location.pathname;
      state.paths[path] = (state.paths[path] || 0) + 1;

      // Trim old entries beyond 60s window
      var cutoff = now - 60000;
      state.pageLoads = state.pageLoads.filter(function (t) { return t > cutoff; });

      Utils.sessionSet(this._storageKey, state);
      return state;
    },

    assess: function () {
      var signals = [];
      var state = this._getState();
      var now = Utils.now();

      // Pages per minute
      var recentLoads = state.pageLoads.filter(function (t) { return t > now - 60000; });
      if (recentLoads.length > CONFIG.rate.maxPagesPerMinute) {
        signals.push({
          score: 20,
          signal: 'high_page_rate: ' + recentLoads.length + '/min'
        });
      }

      // Total pages in session
      if (state.totalPages > CONFIG.rate.maxPagesPerSession) {
        signals.push({
          score: 15,
          signal: 'excessive_session_pages: ' + state.totalPages
        });
      }

      // Rapid consecutive loads (< threshold between pages)
      if (recentLoads.length >= 2) {
        var lastTwo = recentLoads.slice(-2);
        var gap = lastTwo[1] - lastTwo[0];
        if (gap < CONFIG.rate.rapidLoadThresholdMs) {
          signals.push({
            score: 12,
            signal: 'rapid_page_load: ' + gap + 'ms gap'
          });
        }
      }

      // Repeated hits to same path (crawling pattern)
      var path = window.location.pathname;
      if (state.paths[path] && state.paths[path] > 5) {
        signals.push({
          score: 10,
          signal: 'repeated_path: ' + path + ' (' + state.paths[path] + 'x)'
        });
      }

      // Sequential product page crawling detection
      var pathKeys = Object.keys(state.paths);
      var productPaths = pathKeys.filter(function (p) { return /^\/products\//.test(p); });
      if (productPaths.length > 15) {
        signals.push({
          score: 15,
          signal: 'product_crawling: ' + productPaths.length + ' products visited'
        });
      }

      return signals;
    }
  };

  /* =========================================================================
   * 8. SUSPICIOUS QUERY PARAMETER DETECTOR
   * ========================================================================= */
  var QueryDetector = {
    assess: function () {
      var signals = [];
      var params = new URLSearchParams(window.location.search);

      // Unusually large number of query parameters
      var count = 0;
      params.forEach(function () { count++; });
      if (count > 15) {
        signals.push({ score: 8, signal: 'excessive_params: ' + count });
      }

      // Known automation/debug params
      var suspiciousParams = ['__debug', '__test', '__bot', 'headless', 'puppeteer', 'selenium'];
      suspiciousParams.forEach(function (p) {
        if (params.has(p)) {
          signals.push({ score: 15, signal: 'suspicious_param: ' + p });
        }
      });

      return signals;
    }
  };

  /* =========================================================================
   * 9. COOKIE / STORAGE ANOMALY DETECTOR
   * ========================================================================= */
  var StorageDetector = {
    assess: function () {
      var signals = [];

      // Test if cookies work
      var testName = '_jm_ct';
      Utils.setCookie(testName, '1', 60000);
      if (!Utils.getCookie(testName)) {
        signals.push({ score: 8, signal: 'cookies_disabled' });
      }
      // Clean up test cookie
      document.cookie = testName + '=;path=/;max-age=0';

      // Test sessionStorage
      var storageWorks = Utils.safeGet(function () {
        sessionStorage.setItem('_jm_st', '1');
        var v = sessionStorage.getItem('_jm_st');
        sessionStorage.removeItem('_jm_st');
        return v === '1';
      }, false);

      if (!storageWorks) {
        signals.push({ score: 5, signal: 'sessionStorage_disabled' });
      }

      return signals;
    }
  };

  /* =========================================================================
   * 10. TIMING ANOMALY DETECTOR
   * ========================================================================= */
  var TimingDetector = {
    assess: function () {
      var signals = [];

      // Performance timing — if page loaded suspiciously fast
      if (window.performance && performance.timing) {
        var t = performance.timing;
        var loadTime = t.loadEventEnd - t.navigationStart;
        // If loadTime is 0 or negative — timing API tampered
        if (loadTime <= 0 && t.loadEventEnd > 0) {
          signals.push({ score: 10, signal: 'timing_anomaly: loadTime=' + loadTime });
        }
      }

      // Date.now consistency check
      var d1 = Date.now();
      var d2 = new Date().getTime();
      if (Math.abs(d1 - d2) > 1000) {
        signals.push({ score: 15, signal: 'clock_skew: ' + Math.abs(d1 - d2) + 'ms' });
      }

      return signals;
    }
  };

  /* =========================================================================
   * 11. HONEYPOT DETECTOR
   * Inserts invisible elements that only bots interact with.
   * ========================================================================= */
  var HoneypotDetector = {
    _triggered: false,

    init: function () {
      var self = this;

      // Create invisible link/form field
      var hp = document.createElement('a');
      hp.href = '/collections/all?_hp=1';
      hp.textContent = 'View all products';
      hp.setAttribute('tabindex', '-1');
      hp.setAttribute('aria-hidden', 'true');
      hp.style.cssText =
        'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;' +
        'overflow:hidden;opacity:0;pointer-events:none;z-index:-1;';

      hp.addEventListener('click', function (e) {
        e.preventDefault();
        self._triggered = true;
        Logger.warn('honeypot', 'Honeypot link clicked');
      });

      // Insert into page
      var body = document.body;
      if (body) {
        body.appendChild(hp);
      }
    },

    assess: function () {
      if (this._triggered) {
        return [{ score: 30, signal: 'honeypot_triggered' }];
      }
      return [];
    }
  };

  /* =========================================================================
   * 12. RISK ENGINE — aggregates all signals
   * ========================================================================= */
  var RiskEngine = {
    lastScore: 0,
    lastSignals: [],
    lastLevel: 'low',

    evaluate: function () {
      var allSignals = [];

      // Environment (run once, cached)
      if (EnvironmentDetector.signals.length === 0) {
        EnvironmentDetector.runAll();
      }
      allSignals = allSignals.concat(EnvironmentDetector.signals);

      // Behavior
      allSignals = allSignals.concat(BehaviorAnalyzer.assess());

      // Rate limiting
      allSignals = allSignals.concat(RateLimiter.assess());

      // Query params
      allSignals = allSignals.concat(QueryDetector.assess());

      // Storage anomalies
      allSignals = allSignals.concat(StorageDetector.assess());

      // Timing anomalies
      allSignals = allSignals.concat(TimingDetector.assess());

      // Honeypot
      allSignals = allSignals.concat(HoneypotDetector.assess());

      // Traffic source modifier
      var source = TrafficAnalyzer.classify();
      // If traffic is from paid social and has multiple other signals,
      // slightly increase score (the current threat vector)
      if (source === 'meta_paid' && allSignals.length >= 2) {
        allSignals.push({
          score: 5,
          signal: 'paid_social_with_other_signals'
        });
      }

      // Calculate total
      var totalScore = 0;
      for (var i = 0; i < allSignals.length; i++) {
        totalScore += allSignals[i].score;
      }

      // Cap at 100
      totalScore = Math.min(totalScore, 100);

      // Determine level
      var level = 'low';
      if (totalScore >= CONFIG.thresholds.veryHigh)     level = 'very_high';
      else if (totalScore >= CONFIG.thresholds.high)     level = 'high';
      else if (totalScore >= CONFIG.thresholds.medium)   level = 'medium';

      this.lastScore   = totalScore;
      this.lastSignals = allSignals;
      this.lastLevel   = level;

      return { score: totalScore, level: level, signals: allSignals };
    }
  };

  /* =========================================================================
   * 13. CHALLENGE MANAGER
   * Press-and-hold challenge + simple math fallback.
   * Only triggered for high-risk visitors.
   * ========================================================================= */
  var ChallengeManager = {
    _isShowing: false,
    _attempts: 0,

    isVerified: function () {
      return Utils.getCookie('jm_ab_v') === '1';
    },

    _markVerified: function () {
      Utils.setCookie('jm_ab_v', '1', CONFIG.challenge.verifiedTTLMs);
      Logger.info('challenge', 'Visitor verified via challenge');
    },

    show: function (riskData) {
      if (this._isShowing || this.isVerified()) return;
      this._isShowing = true;
      this._attempts++;

      Logger.warn('challenge', 'Showing challenge', {
        attempt: this._attempts,
        score: riskData.score,
        level: riskData.level
      });

      if (this._attempts > CONFIG.challenge.maxAttemptsBeforeBlock) {
        this._showSoftBlock(riskData);
        return;
      }

      this._renderChallenge(riskData);
    },

    _renderChallenge: function (riskData) {
      var self = this;

      // Overlay
      var overlay = document.createElement('div');
      overlay.id = 'jm-ab-challenge';
      overlay.className = 'jm-ab-overlay';

      // Generate simple math
      var a = Math.floor(Math.random() * 10) + 1;
      var b = Math.floor(Math.random() * 10) + 1;
      var correctAnswer = a + b;

      overlay.innerHTML =
        '<div class="jm-ab-dialog">' +
          '<div class="jm-ab-dialog-header">' +
            '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
              '<path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm-1-5h2v2h-2v-2zm0-8h2v6h-2V7z" fill="currentColor"/>' +
            '</svg>' +
            '<span>Quick Verification</span>' +
          '</div>' +
          '<p class="jm-ab-dialog-text">To continue browsing, please answer this quick question:</p>' +
          '<p class="jm-ab-math-q">What is <strong>' + a + ' + ' + b + '</strong>?</p>' +
          '<div class="jm-ab-input-row">' +
            '<input type="number" id="jm-ab-answer" class="jm-ab-input" autocomplete="off" placeholder="Your answer" />' +
            '<button id="jm-ab-submit" class="jm-ab-btn">Verify</button>' +
          '</div>' +
          '<p class="jm-ab-dialog-footer">This helps us protect against automated traffic.</p>' +
        '</div>';

      document.body.appendChild(overlay);

      // Focus trap
      var input = document.getElementById('jm-ab-answer');
      var btn   = document.getElementById('jm-ab-submit');

      setTimeout(function () { if (input) input.focus(); }, 100);

      var handleVerify = function () {
        var val = parseInt(input.value, 10);
        if (val === correctAnswer) {
          self._markVerified();
          self._dismiss(overlay);
        } else {
          input.value = '';
          input.placeholder = 'Incorrect, try again';
          input.classList.add('jm-ab-input-error');
          setTimeout(function () { input.classList.remove('jm-ab-input-error'); }, 600);
        }
      };

      btn.addEventListener('click', handleVerify);
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') handleVerify();
      });
    },

    _showSoftBlock: function (riskData) {
      Logger.error('challenge', 'Soft block — too many failed attempts', {
        attempts: this._attempts,
        score: riskData.score
      });

      // Store temp block
      Utils.sessionSet('jm_ab_blocked', {
        ts: Utils.now(),
        until: Utils.now() + CONFIG.block.tempBlockDurationMs
      });

      var overlay = document.createElement('div');
      overlay.id = 'jm-ab-challenge';
      overlay.className = 'jm-ab-overlay';

      overlay.innerHTML =
        '<div class="jm-ab-dialog">' +
          '<div class="jm-ab-dialog-header">' +
            '<span>Access Temporarily Limited</span>' +
          '</div>' +
          '<p class="jm-ab-dialog-text">' +
            'We have detected unusual activity from your session. ' +
            'Please try again in a few minutes.' +
          '</p>' +
          '<p class="jm-ab-dialog-footer">' +
            'If you believe this is an error, please contact us at help@junemoon.in' +
          '</p>' +
        '</div>';

      document.body.appendChild(overlay);
    },

    _dismiss: function (overlay) {
      this._isShowing = false;
      if (overlay && overlay.parentNode) {
        overlay.style.opacity = '0';
        setTimeout(function () {
          if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        }, 300);
      }
    },

    isSoftBlocked: function () {
      var block = Utils.sessionGet('jm_ab_blocked');
      if (block && block.until > Utils.now()) return true;
      return false;
    }
  };

  /* =========================================================================
   * 14. DEBUG PANEL (only when debugMode = true)
   * ========================================================================= */
  var DebugPanel = {
    _el: null,

    init: function () {
      if (!CONFIG.logging.debugMode) return;

      var panel = document.createElement('div');
      panel.id = 'jm-ab-debug';
      panel.style.cssText =
        'position:fixed;bottom:10px;right:10px;z-index:999999;' +
        'background:rgba(0,0,0,0.88);color:#0f0;font-family:monospace;' +
        'font-size:11px;padding:10px;border-radius:6px;max-width:360px;' +
        'max-height:300px;overflow-y:auto;pointer-events:auto;line-height:1.5;' +
        'box-shadow:0 2px 12px rgba(0,0,0,0.4);';
      panel.innerHTML = '<strong style="color:#ff0">JM AntiBot Debug</strong><br>';
      document.body.appendChild(panel);
      this._el = panel;
    },

    update: function (riskData) {
      if (!this._el) return;

      var src = TrafficAnalyzer.classify();
      var utm = TrafficAnalyzer.getUTMData();
      var signalList = riskData.signals.map(function (s) {
        return '  [+' + s.score + '] ' + s.signal;
      }).join('<br>');

      this._el.innerHTML =
        '<strong style="color:#ff0">JM AntiBot v' + CONFIG.version + '</strong><br>' +
        '<span style="color:#aaa">Source:</span> ' + src + '<br>' +
        '<span style="color:#aaa">UTM:</span> ' + (utm.source || '-') + '/' + (utm.medium || '-') + '<br>' +
        '<span style="color:#aaa">Score:</span> <strong style="color:' +
          (riskData.score >= CONFIG.thresholds.high ? '#f44' :
           riskData.score >= CONFIG.thresholds.medium ? '#fa0' : '#0f0') +
        '">' + riskData.score + '/100</strong> (' + riskData.level + ')<br>' +
        '<span style="color:#aaa">Verified:</span> ' + ChallengeManager.isVerified() + '<br>' +
        '<span style="color:#aaa">Signals:</span><br>' +
        (signalList || '  (none)');
    }
  };

  /* =========================================================================
   * 15. MAIN CONTROLLER
   * ========================================================================= */
  var Controller = {
    init: function () {
      // 0. Skip if on checkout or admin (theme JS shouldn't run there, but guard)
      var path = window.location.pathname;
      if (/^\/(checkout|admin|services)/.test(path)) return;

      // 1. Check for existing soft block
      if (ChallengeManager.isSoftBlocked()) {
        ChallengeManager._showSoftBlock({ score: 100 });
        Logger.warn('controller', 'Returning blocked visitor');
        return;
      }

      // 2. Already verified? Skip heavy checks
      var isVerified = ChallengeManager.isVerified();

      // 3. Record page load for rate limiting
      RateLimiter.record();

      // 4. Init behavior tracking
      BehaviorAnalyzer.init();

      // 5. Init honeypot
      HoneypotDetector.init();

      // 6. Init debug panel
      DebugPanel.init();

      // 7. Run environment checks immediately
      EnvironmentDetector.runAll();

      // 8. Log the visit
      Logger.info('visit', 'Page load', {
        path:     path,
        source:   TrafficAnalyzer.classify(),
        utmData:  TrafficAnalyzer.getUTMData(),
        verified: isVerified
      });

      // 9. First risk assessment (delayed to allow behavior signals)
      var self = this;
      setTimeout(function () {
        self._assess(isVerified);
      }, CONFIG.timing.firstCheckDelayMs);

      // 10. Periodic re-assessment
      setInterval(function () {
        self._assess(isVerified);
      }, CONFIG.timing.recheckIntervalMs);
    },

    _assess: function (isVerified) {
      var result = RiskEngine.evaluate();

      // Update debug panel
      DebugPanel.update(result);

      // Log assessment
      Logger.info('risk', 'Assessment', {
        score:   result.score,
        level:   result.level,
        signals: result.signals.map(function (s) { return s.signal; }),
        verified: isVerified
      });

      // Action based on risk level
      if (isVerified) {
        // Verified visitors only get blocked at very_high (re-offense)
        if (result.level === 'very_high') {
          Logger.error('action', 'Verified visitor re-scored very_high', { score: result.score });
          // Don't re-challenge, but log for review
        }
        return;
      }

      switch (result.level) {
        case 'low':
          // Normal access
          break;

        case 'medium':
          // Silent monitoring — already logged above
          break;

        case 'high':
          // Show challenge
          ChallengeManager.show(result);
          break;

        case 'very_high':
          // Show challenge (will escalate to soft block if repeated)
          ChallengeManager.show(result);
          break;
      }
    }
  };

  /* =========================================================================
   * 16. BOOT
   * ========================================================================= */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { Controller.init(); });
  } else {
    Controller.init();
  }

})();